import { BookingOrigin } from '@utriper/core';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useCurrencies } from './currency';
import { ReportService } from 'core/api/services/reportsService';

const ReportContext = React.createContext(null);

export const ReportProvider = ({ children }) => {
    const { currency, currencies, getCurrencies } = useCurrencies();
    const [loadingApp, setLoadingApp] = useState(false);
    const [loadingHost, setLoadingHost] = useState(false);
    const [appReports, setAppReports] = useState(null);
    const [hostReports, setHostReports] = useState(null);

    const [filters, setFilters] = useState({
        startDate: DateTime.utc().startOf('month'),
        endDate: DateTime.utc().endOf('month'),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota',
        paymentStatus: [],
        originIds: [],
        hostIds: [],
        experienceIds: [],
        experiencerIds: [],
        paymentMethods: [],
    });
    const [view, setView] = useState('app');
    const [statusFilters, setStatusFilters] = useState([]);
    const { t: translate, i18n } = useTranslation();

    useEffect(() => {
        if (!currency) return;

        getCurrencies({ code: currency });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    const dayLabels = [translate('reports.day_labels.monday'), translate('reports.day_labels.tuesday'), translate('reports.day_labels.wednesday'), translate('reports.day_labels.thursday'), translate('reports.day_labels.friday'), translate('reports.day_labels.saturday'), translate('reports.day_labels.sunday')];
    const monthsMap = {
        january: translate('reports.months_labels.january'),
        february: translate('reports.months_labels.february'),
        march: translate('reports.months_labels.march'),
        april: translate('reports.months_labels.april'),
        may: translate('reports.months_labels.may'),
        june: translate('reports.months_labels.june'),
        july: translate('reports.months_labels.july'),
        august: translate('reports.months_labels.august'),
        september: translate('reports.months_labels.september'),
        october: translate('reports.months_labels.october'),
        november: translate('reports.months_labels.november'),
        december: translate('reports.months_labels.december'),
    };

    const buildRateMap = (rates = [], overrideRates = []) => {
        if (!Array.isArray(rates) && !Array.isArray(overrideRates)) return { USD: 1 };

        return [...(Array.isArray(rates) ? rates : []), ...(Array.isArray(overrideRates) ? overrideRates : [])].reduce(
            (acc, { code, rate }) => {
                if (code && typeof rate === 'number' && rate !== 0) {
                    acc[code] = rate;
                }
                return acc;
            },
            { USD: 1 }
        );
    };

    const roundValue = (amount) => {
        if (typeof amount !== 'number' || Number.isNaN(amount)) {
            return amount;
        }

        return Math.round((amount + Number.EPSILON) * 100) / 100;
    };

    // Converts a currency prices object into the selected currency.
    const convertValueToCurrency = (prices, rates) => {
        // Return early if prices is null or undefined
        if (prices === null || prices === undefined) {
            return prices;
        }

        // If the value is an object (e.g. { USD: 10, COP: 2000 })
        if (typeof prices === 'object') {
            const rateMap = buildRateMap(rates, currencies);
            const targetCurrency = currency || 'USD';

            // Convert all amounts to USD
            let totalInUsd = 0;
            for (const code in prices) {
                const amount = prices[code];
                const sourceRate = code === 'USD' ? 1 : rateMap?.[code];

                // If rate is missing or invalid, just add the raw amount
                if (!sourceRate || sourceRate === 0) {
                    totalInUsd += amount;
                } else {
                    totalInUsd += amount / sourceRate;
                }
            }

            // Convert USD total to target currency
            const targetRate = targetCurrency === 'USD' ? 1 : rateMap?.[targetCurrency];

            if (!targetRate || targetRate === 0) {
                return roundValue(totalInUsd);
            }

            // Round and return the final result
            return roundValue(totalInUsd * targetRate);
        }

        // If the value is a single number or string, parse and round it
        const parsed = Number(prices);
        return Number.isNaN(parsed) ? 0 : roundValue(parsed);
    };

    const getValuesByOrigin = (object, key, rates) => {
        if (!object || !key) return [];

        const values = object[key] ?? {};

        return (
            Object.entries(values)?.map(([key, value]) => ({
                name: BookingOrigin.getTranslatedName(BookingOrigin.types, BookingOrigin.IDS[key], i18n.language)?.[0],
                value: convertValueToCurrency(value, rates),
            })) ?? []
        );
    };

    const getValues = (object, key, rates) => {
        if (!object || !key) return [];

        const values = object[key] ?? {};

        return (
            Object.entries(values)?.map(([key, value]) => ({
                name: key,
                value: convertValueToCurrency(value, rates),
            })) ?? []
        );
    };

    const getDateValues = (object) => {
        if (!object) return [];
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        const series = [
            {
                name: translate('reports.bookings'),
                data: dayOrder.map((day) => object[day] || 0),
            },
        ];

        return series;
    };

    const getMonthlyValues = (monthly) => {
        if (!monthly) return [];

        const monthlyEvolution = Object.keys(monthly).sort((a, b) => {
            const monthOrder = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
            return monthOrder.indexOf(a) - monthOrder.indexOf(b);
        });

        return monthlyEvolution;
    };

    const getTotalsApp = (totals, rates) => {
        const auxTotals = [
            {
                title: translate('reports.app_report.totals.total_tickets'),
                value: totals?.totalTickets,
            },
            {
                title: translate('reports.app_report.totals.total_price'),
                value: convertValueToCurrency(totals?.totalPrice, rates),
                currency,
            },
            {
                title: translate('reports.app_report.totals.total_paid_price'),
                value: convertValueToCurrency(totals?.totalPaidPrice, rates),
                currency,
            },
            {
                title: translate('reports.app_report.totals.host_commission'),
                value: convertValueToCurrency(totals?.hostCommission, rates),
                currency,
            },
            {
                title: translate('reports.app_report.totals.utriper_commission'),
                value: convertValueToCurrency(totals?.utriperCommission, rates),
                currency,
            },
        ];

        return auxTotals ?? [];
    };

    const getTotalsHost = (totals, rates) => {
        const auxTotals = [
            {
                title: translate('reports.host_report.totals.total_tickets'),
                value: totals?.totalTickets,
            },
            {
                title: translate('reports.host_report.totals.total_price'),
                value: convertValueToCurrency(totals?.totalPrice, rates),
                currency,
            },
            {
                title: translate('reports.host_report.totals.total_paid_price'),
                value: convertValueToCurrency(totals?.totalPaidPrice, rates),
                currency,
            },
            {
                title: translate('reports.host_report.totals.total_commission'),
                value: convertValueToCurrency(totals?.totalCommission, rates),
                currency,
            },
            {
                title: translate('reports.host_report.totals.host_commission'),
                value: convertValueToCurrency(totals?.hostCommission, rates),
                currency,
            },
            {
                title: translate('reports.host_report.totals.utriper_commission'),
                value: convertValueToCurrency(totals?.utriperCommission, rates),
                currency,
            },
        ];

        return auxTotals ?? [];
    };

    const updateFilters = (newFilters) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...newFilters,
        }));
    };

    const updateFilter = (key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    const resetFilters = () => {
        setFilters({
            startDate: DateTime.utc().startOf('month'),
            endDate: DateTime.utc().endOf('month'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota',
            paymentStatus: [],
            originIds: [],
            hostIds: [],
            experienceIds: [],
            experiencerIds: [],
            paymentMethods: [],
        });
        setStatusFilters([]);
    };

    const getHostReports = async (options = {}) => {
        try {
            setLoadingHost(true);
            const requestOptions = {
                ...options,
                filters: { ...filters, ...options.filters },
                statusFilters: statusFilters.length > 0 ? statusFilters : options.statusFilters,
            };

            const result = await ReportService.getHostReports(requestOptions);

            if (!result) {
                throw new Error();
            }
            await new Promise((resolve) => setTimeout(resolve, 3000));
            setHostReports(result);

            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingHost(false);
        }
    };

    const getAppReports = async (options = {}) => {
        try {
            setLoadingApp(true);
            const requestOptions = {
                ...options,
                filters: { ...filters, ...options.filters },
                statusFilters: statusFilters.length > 0 ? statusFilters : options.statusFilters,
            };

            const result = await ReportService.getAppReports(requestOptions);

            if (!result) {
                throw new Error();
            }

            await new Promise((resolve) => setTimeout(resolve, 3000));
            setAppReports(result);

            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingApp(false);
        }
    };

    return (
        <ReportContext.Provider
            value={{
                loadingApp,
                loadingHost,
                hostReports,
                appReports,
                filters,
                statusFilters,
                getHostReports,
                getAppReports,
                updateFilters,
                updateFilter,
                resetFilters,
                setStatusFilters,
                setHostReports,
                setAppReports,
                setFilters,
                view,
                setView,
                dayLabels,
                monthsMap,
                getValuesByOrigin,
                getValues,
                getDateValues,
                getMonthlyValues,
                getTotalsApp,
                getTotalsHost,
            }}>
            {children}
        </ReportContext.Provider>
    );
};

ReportProvider.displayName = 'ReportProvider';

export const useReport = () => useContext(ReportContext);
